<template>
  <div>
    <b-table
      responsive="sm"
      :fields="fields"
      :items="dataList"
      striped
      :busy="loading"
      :empty-text="emptyText"
      :show-empty="!!emptyText"
    >
      <template #table-busy>
        <div class="text-center text-primary my-2">
          <b-spinner class="align-middle" />
          <strong class="ml-2">{{ $t('common.loading') }}</strong>
        </div>
      </template>
      <template #cell(description)="data">
        <span>{{ truncate(data.item.description, 80) }}</span>
      </template>
      <template #cell(status)="data">
        <b-badge :variant="data.item.status === 1 || data.item.status === '1' ? 'light-success' : 'light-danger'">
          {{ data.item.status === 1 || data.item.status === '1' ? $t('common.active') : $t('common.passive') }}
        </b-badge>
      </template>
      <template #cell(control)="data">
        <div class="text-right">
          <b-dropdown
            split
            variant="outline-primary"
            right
            size="sm"
            @click="$router.push('/app/guides/edit/' + data.item.id)"
          >
            <template #button-content>
              <feather-icon icon="EditIcon" />
              {{ $t('common.update') }}
            </template>
            <b-dropdown-item
              @click="$store.dispatch('guides/openCreateTaskModal', data.item)"
            >
              <feather-icon icon="FileTextIcon" />
              <span
                class="align-middle ml-50"
                v-text="$t('guides.createTask.action')"
              />
            </b-dropdown-item>
            <b-dropdown-divider />
            <b-dropdown-item
              @click="removeData(data.item.id)"
            >
              <feather-icon icon="TrashIcon" />
              <span
                class="align-middle ml-50"
                v-text="$t('common.delete')"
              />
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>
    </b-table>
    <b-card-footer>
      <b-pagination
        v-if="dataCounts > perPage"
        v-model="currentPage"
        :total-rows="dataCounts"
        :per-page="perPage"
        first-number
        last-number
        prev-class="prev-item"
        next-class="next-item"
      >
        <template #prev-text>
          <feather-icon
            icon="ChevronLeftIcon"
            size="18"
          />
        </template>
        <template #next-text>
          <feather-icon
            icon="ChevronRightIcon"
            size="18"
          />
        </template>
      </b-pagination>
    </b-card-footer>
    <create-task-from-guide-modal />
  </div>
</template>

<script>
import {
  BTable, BPagination, BCardFooter, BBadge, BSpinner, BDropdown, BDropdownItem, BDropdownDivider,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import CreateTaskFromGuideModal from './CreateTaskFromGuideModal.vue'

const tableName = 'guides'
export default {
  name: 'GuidesTable',
  components: {
    BTable,
    BPagination,
    BCardFooter,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    CreateTaskFromGuideModal,
    BBadge,
    BSpinner,
  },
  data() {
    return {
      currentPage: 1,
      perPage: this.$store.state.app.perPage,
      dataQuery: {
        select: [
          `${tableName}.id AS id`,
          `${tableName}.title AS title`,
          `${tableName}.description AS description`,
          `${tableName}.task_type AS task_type`,
          `${tableName}.status AS status`,
        ],
        limit: this.$store.state.app.perPage,
        start: 0,
      },
    }
  },
  computed: {
    // Dil değişince sütun başlıkları da güncellensin diye computed
    fields() {
      return [
        {
          key: 'title',
          label: this.$t('fields.title'),
        },
        {
          key: 'task_type',
          label: this.$t('fields.taskType'),
          thClass: 'width-200',
        },
        {
          key: 'status',
          label: this.$t('fields.status'),
          thClass: 'width-200',
        },
        {
          key: 'control',
          label: '',
          thClass: 'width-200',
        },
      ]
    },
    loading() {
      return this.$store.getters['guides/loading']
    },
    emptyText() {
      if (this.loading) return this.$t('common.pleaseWait')
      if (this.dataList.length === 0) return this.$t('common.noData')
      return null
    },
    createTaskGuide() {
      return this.$store.getters['guides/createTaskGuide']
    },
    dataList() {
      return this.$store.getters['guides/dataList']
    },
    dataCounts() {
      return this.$store.getters['guides/dataCounts']
    },
    saveData() {
      return this.$store.getters['guides/dataSaveStatus']
    },
  },
  watch: {
    currentPage(val) {
      this.dataQuery.start = (val - 1) * this.perPage
      this.getDataList()
    },
    saveData(val) {
      if (val?.status === true) {
        this.showToast({
          variant: 'success', title: this.$t('common.toast.success'), icon: 'CheckIcon', text: val.message,
        })
      } else if (val?.status === false) {
        this.showToast({
          variant: 'danger', title: this.$t('common.toast.error'), icon: 'XIcon', text: val.message,
        })
      }
      this.getDataList()
    },
  },
  created() {
    this.getDataList()
  },
  methods: {
    getDataList() {
      this.$store.dispatch('guides/getDataList', this.dataQuery)
    },
    removeData(id) {
      this.$swal({
        title: this.$t('alerts.remove.title'),
        text: this.$t('alerts.remove.message'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('alerts.remove.confirm'),
        cancelButtonText: this.$t('alerts.remove.cancel'),
        customClass: { confirmButton: 'btn btn-success', cancelButton: 'btn btn-danger ml-1' },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.$store.dispatch('guides/removeData', id)
          this.$swal({
            icon: 'success',
            title: this.$t('alerts.remove.resultTitle'),
            text: this.$t('alerts.remove.resultMessage'),
            confirmButtonText: this.$t('alerts.remove.resultClose'),
            customClass: { confirmButton: 'btn btn-success' },
          })
        }
      })
    },
    truncate(str, len) {
      if (!str) return '-'
      return str.length > len ? `${str.substring(0, len)}...` : str
    },
    showToast(params) {
      this.$toast({ component: ToastificationContent, props: params })
    },
  },
}
</script>
